/* BMN Quote Assistant v1 - web chat widget
 *
 * Depends on:
 *   window.BMNQuoteBrain (from quote-brain.js) - channel-agnostic core
 *
 * Two mount modes, controlled by data-mount on #bmn-quote-root:
 *   "floating" - FAB + slide-up panel (site-wide, default)
 *   "page"     - inline full-page layout (for /pages/get-a-quote)
 *
 * LLM path: POSTs to the Netlify proxy configured on the mount node.
 * If the proxy returns anything other than a well-formed completion,
 * the widget silently falls back to the deterministic brain so the
 * gate and lead capture always work. This means the moment
 * ANTHROPIC_API_KEY is set on the bmn-ai-advisor-proxy Netlify site
 * and the function is redeployed, the LLM path lights up on its own.
 *
 * Lead delivery: POSTs the lead to the endpoint on data-lead-url. If that URL
 * is a HubSpot Forms submit endpoint (hsforms.com) the payload is shaped as
 * HubSpot form fields; otherwise a generic JSON lead is sent. A JSON copy is
 * always stashed in sessionStorage as a failsafe.
 *
 * NOTE: Shopify's /contact endpoint is NOT usable on this store. brandmenow.co.uk
 * sits behind Cloudflare, which challenges POST /contact and then 404s, so no
 * client-side submit (fetch, iframe, or top-level) can deliver a lead through it.
 * That is why the lead goes to a configured browser-friendly endpoint instead.
 * The path is decoupled from the LLM/proxy: leads deliver even with the proxy down.
 */
(function () {
  'use strict';

  var Brain = window.BMNQuoteBrain;
  if (!Brain) { console.warn('[BMN Quote] Brain not loaded'); return; }

  var roots = document.querySelectorAll('#bmn-quote-root, .bmn-quote-root');
  if (!roots.length) return;

  for (var i = 0; i < roots.length; i++) {
    try { mount(roots[i]); } catch (err) { console.warn('[BMN Quote] mount failed', err); }
  }

  function mount(root) {
    var mode = (root.getAttribute('data-mount') || 'floating').toLowerCase();
    var proxyUrl = root.getAttribute('data-proxy-url') || '';
    var leadUrl = root.getAttribute('data-lead-url') || '';
    var configUrl = root.getAttribute('data-config-url') || '';
    var agentName = root.getAttribute('data-agent-name') || 'BMN Quote Assistant';

    // Guard against double mount.
    if (root.dataset.bmnMounted === '1') return;
    root.dataset.bmnMounted = '1';

    var state = {
      rawText: '',
      garments: [],
      methods: [],
      positions: [],
      sector: '',
      isReorder: false,
      qty: null,
      colour: null,
      sizes: {}
    };
    var config = { garments: [] };
    var configReady = false;
    var configPromise = null;
    var conversation = [];   // [{ role: 'user'|'assistant', content: string }]
    var loading = false;
    var proxyDegraded = false;
    var stage = 'chat';      // "chat" | "gate" | "done"
    var quickChipsSuppressed = false;

    // Kick off config load early.
    if (configUrl) {
      configPromise = fetch(configUrl, { credentials: 'omit' })
        .then(function (r) { return r.json(); })
        .then(function (data) { config = data; configReady = true; return data; })
        .catch(function (err) {
          console.warn('[BMN Quote] config load failed', err);
          configReady = true; // proceed with empty catalogue rather than block.
          return config;
        });
    } else {
      configReady = true;
    }

    // Build DOM.
    var host = document.createElement('div');
    host.className = 'bmn-q-host bmn-q-host--' + mode;
    host.innerHTML = renderShell(mode, agentName);
    root.appendChild(host);

    var fab = host.querySelector('.bmn-q-fab');
    var panel = host.querySelector('.bmn-q-panel, .bmn-q-page');
    var closeBtn = host.querySelector('.bmn-q-close');
    var msgsEl = host.querySelector('.bmn-q-msgs');
    var typingEl = host.querySelector('.bmn-q-typing');
    var quickEl = host.querySelector('.bmn-q-quick');
    var inputEl = host.querySelector('.bmn-q-input:not([data-gate])');
    var sendEl = host.querySelector('.bmn-q-send');
    var gateEl = host.querySelector('.bmn-q-gate');
    var gateForm = host.querySelector('.bmn-q-gate form');
    var gateSubmit = host.querySelector('.bmn-q-btn--submit');

    if (fab) fab.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (sendEl) sendEl.addEventListener('click', function () { handleUser(inputEl.value); });
    if (inputEl) {
      inputEl.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleUser(inputEl.value); }
      });
    }
    if (gateForm) gateForm.addEventListener('submit', function (e) { e.preventDefault(); submitLead(); });

    if (mode === 'page') { open(); } // full page opens immediately

    function open() {
      if (fab) fab.style.display = 'none';
      if (panel) panel.classList.add('is-open');
      if (!conversation.length) initChat();
      setTimeout(function () { if (inputEl) inputEl.focus(); }, 200);
    }
    function close() {
      if (panel) panel.classList.remove('is-open');
      if (fab && mode === 'floating') fab.style.display = 'inline-flex';
    }

    async function initChat() {
      var greeting = 'Hi, I can price up your workwear right now. Tell me what you need. A garment, rough quantity, and how you would like it branded.';
      addBot(greeting);
      conversation.push({ role: 'assistant', content: greeting });
      renderQuickReplies(['Polo shirts', 'Hoodies', 'T-shirts', 'Softshells', 'Caps']);
    }

    async function handleUser(raw) {
      var text = (raw || '').trim();
      if (!text || loading) return;
      inputEl.value = '';
      clearQuickReplies();
      addUser(text);
      conversation.push({ role: 'user', content: text });

      // Wait for config on the first turn if still loading.
      if (!configReady && configPromise) {
        setLoading(true);
        await configPromise;
        setLoading(false);
      }

      // Update state deterministically. This is always ground truth for the gate.
      var parsed = Brain.parse(text, config);
      state = Brain.mergeState(state, parsed);

      // Attempt the LLM path first for a natural reply. Fall back silently.
      setLoading(true);
      var llmReply = null;
      if (proxyUrl && !proxyDegraded) {
        try { llmReply = await callProxy(); }
        catch (err) { proxyDegraded = true; console.warn('[BMN Quote] LLM path degraded', err); }
      }
      setLoading(false);

      var next = Brain.nextQuestion(state, config);
      var matched = Brain.mapGarments(state, config);

      // If we hit a matched garment for the first time, always show blank price.
      if (matched.length && !state._shownPricesFor) state._shownPricesFor = {};
      var newPrices = [];
      for (var m = 0; m < matched.length; m++) {
        if (state._shownPricesFor && !state._shownPricesFor[matched[m].baseCode]) {
          newPrices.push(matched[m]);
          state._shownPricesFor[matched[m].baseCode] = 1;
        }
      }

      var replyText = llmReply || composeDeterministicReply(state, next, matched, newPrices);

      // Guardrail: strip em dashes and any invented decorated £ figures the LLM
      // may have hallucinated in violation of the system prompt.
      replyText = sanitiseReply(replyText, matched);

      addBot(replyText);
      conversation.push({ role: 'assistant', content: replyText });

      // Render price blocks for newly-priced garments.
      if (newPrices.length) renderPriceBlocks(Brain.priceBlanks(newPrices));

      // Are we ready to gate?
      if (!next) {
        openGate();
      } else {
        renderQuickReplies(quickRepliesFor(next.field));
      }
    }

    function composeDeterministicReply(st, nextQ, matched, newPrices) {
      var parts = [];
      if (newPrices.length === 1) {
        parts.push('Nice, ' + newPrices[0].name + ' starts at £' + newPrices[0].blankFrom.toFixed(2) + ' each, ex VAT. Decoration is quoted separately.');
      } else if (newPrices.length > 1) {
        parts.push('Great, I have blank "from" prices for those below. Decoration is quoted separately.');
      } else if (matched.length && !newPrices.length) {
        // Nothing new to price this turn.
      }
      if (nextQ) {
        parts.push(nextQ.prompt);
      } else {
        parts.push('I have enough to put your itemised branded quote together. Pop your name and email in below and it will land in your inbox, usually within one working day. Turnaround is ' + Brain.consts.TURNAROUND + '.');
      }
      return parts.join('\n\n');
    }

    function sanitiseReply(text, matched) {
      var out = String(text || '');
      // Kill em / en dashes per house style.
      out = out.replace(/[, –]/g, ', ');
      // Kill "£12.34 each printed / embroidered" style hallucinations. We only
      // ever show blankFrom values; anything else that mentions £ near a
      // decoration word is a hallucination.
      out = out.replace(/£\s*\d+(?:\.\d{1,2})?\s*(?:each|per|inc(?:l)?|including)?\s*(?:printed|embroidered|decorated|with logo|branded)/gi,
        'itemised branded quote (sent by email)');
      return out;
    }

    async function callProxy() {
      var systemPrompt = buildSystemPrompt();
      // Trim conversation to last 12 turns to keep the request tight.
      var recent = conversation.slice(-12);
      var body = {
        system: systemPrompt,
        messages: recent
      };
      var ctrl = new AbortController();
      var t = setTimeout(function () { ctrl.abort(); }, 15000);
      var r;
      try {
        r = await fetch(proxyUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          signal: ctrl.signal
        });
      } finally { clearTimeout(t); }
      if (!r.ok) throw new Error('proxy status ' + r.status);
      var d = await r.json();
      if (d && d.error) throw new Error('proxy error ' + d.error);
      var content = (d && d.content && d.content[0] && d.content[0].text) || '';
      if (!content) throw new Error('proxy empty completion');
      return content;
    }

    function buildSystemPrompt() {
      var priceList = (config.garments || []).map(function (g) {
        return '- ' + g.baseCode + ' ' + g.name + ' (' + g.vendor + '): from £' + g.blankFrom.toFixed(2) + ' ex VAT';
      }).join('\n');
      return [
        'You are the Brand Me Now Quote Assistant. Brand Me Now is a UK B2B custom workwear and garment decoration specialist since 1999, based in Selly Oak Birmingham. In-house embroidery (36-head) and DTF printing plus screen print, DTG, heat transfer, sublimation.',
        'Voice: plain UK English, warm and brief, two to three sentences per turn. Do NOT use em dashes. Ask ONE question at a time.',
        'Turnaround is 7 to 10 working days. Never invent a different turnaround. Store is tax exclusive, all prices ex VAT.',
        'You may quote these blank "from" prices verbatim, always ex VAT, always labelled "from":',
        priceList || '(no catalogue loaded)',
        'You must NEVER invent decorated or all-in prices. If the customer asks for the total including printing or embroidery, tell them the team will send an itemised branded quote once you have their name and email, usually within one working day.',
        'Never expose trade costs, wholesale, or supplier information.',
        'When the customer has provided garment, quantity, decoration method, and position, stop asking questions and tell them to enter their name and email to receive the itemised branded quote.'
      ].join('\n');
    }

    function renderPriceBlocks(rows) {
      for (var i = 0; i < rows.length; i++) {
        var row = rows[i];
        var row2 = document.createElement('div');
        row2.className = 'bmn-q-row bmn-q-row--bot';
        row2.innerHTML = '<div class="bmn-q-bot-av">B</div>' +
          '<div class="bmn-q-price"><strong>' + esc(row.name) + '</strong>: from £' + row.displayFrom.toFixed(2) +
          ' each<small>' + esc(row.note) + '</small></div>';
        msgsEl.appendChild(row2);
      }
      msgsEl.scrollTop = msgsEl.scrollHeight;
    }

    function quickRepliesFor(field) {
      if (field === 'garment')   return ['Polo shirts', 'Hoodies', 'T-shirts', 'Softshells', 'Caps'];
      if (field === 'qty')       return ['10', '25', '50', '100', '250'];
      if (field === 'method')    return ['Embroidery', 'DTF print', 'Both'];
      if (field === 'positions') return ['Left chest', 'Back', 'Sleeve', 'Front'];
      return [];
    }
    function renderQuickReplies(opts) {
      if (!quickEl || quickChipsSuppressed) return;
      quickEl.innerHTML = '';
      for (var i = 0; i < opts.length; i++) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'bmn-q-chip';
        b.textContent = opts[i];
        (function (val) { b.addEventListener('click', function () { handleUser(val); }); })(opts[i]);
        quickEl.appendChild(b);
      }
    }
    function clearQuickReplies() { if (quickEl) quickEl.innerHTML = ''; }

    function openGate() {
      if (!gateEl) return;
      stage = 'gate';
      quickChipsSuppressed = true;
      clearQuickReplies();
      gateEl.classList.add('is-on');
      var nameField = gateEl.querySelector('[data-gate="name"]');
      if (nameField) nameField.focus();
    }

    async function submitLead() {
      var contact = {
        name:    (gateEl.querySelector('[data-gate="name"]')    || {}).value || '',
        email:   (gateEl.querySelector('[data-gate="email"]')   || {}).value || '',
        company: (gateEl.querySelector('[data-gate="company"]') || {}).value || '',
        phone:   (gateEl.querySelector('[data-gate="phone"]')   || {}).value || '',
        notes:   (gateEl.querySelector('[data-gate="notes"]')   || {}).value || ''
      };
      // A long name means the enquiry was typed into the Name box.
      // Keep the first two words as the name and carry the rest into the message.
      contact.extra = '';
      var nameWords = (contact.name || '').trim().split(/\s+/);
      if (nameWords.length > 3 || (contact.name || '').length > 40) {
        contact.extra = nameWords.slice(2).join(' ');
        contact.name = nameWords.slice(0, 2).join(' ');
      }
      var val = Brain.validateContact(contact);
      var nameErr = gateEl.querySelector('[data-error="name"]');
      var emailErr = gateEl.querySelector('[data-error="email"]');
      if (nameErr) nameErr.textContent = val.errors.name || '';
      if (emailErr) emailErr.textContent = val.errors.email || '';
      if (!val.ok) return;

      gateSubmit.disabled = true;
      gateSubmit.textContent = 'Sending...';

      var matched = Brain.mapGarments(state, config);
      var payload = Brain.buildLeadPayload(state, contact, matched);
      var bodyText = Brain.formatLeadEmailBody(payload);

      var ok = await deliverLead(contact, payload, bodyText);

      gateSubmit.disabled = false;
      gateSubmit.textContent = 'Send my quote request';

      if (ok) {
        try { if (window.bmnLead) window.bmnLead('quote_widget'); } catch (e) {}
        stage = 'done';
        gateEl.classList.remove('is-on');
        addBot('Thanks ' + firstName(contact.name) + '. Your quote request is on its way. The team will send your itemised branded quote to ' + contact.email + ', usually within one working day. Turnaround is ' + Brain.consts.TURNAROUND + '. Anything else I can price up?');
        renderQuickReplies(['Add another garment', 'New enquiry']);
        // Reset per-garment shown-prices so a second enquiry works.
        state._shownPricesFor = {};
      } else {
        addBot('Something went wrong sending that through. Please email us on ' + Brain.consts.CONTACT_EMAIL + ' or call ' + Brain.consts.CONTACT_PHONE + ' and we will sort it. Sorry about that.');
      }
    }

    async function deliverLead(contact, payload, bodyText) {
      // Failsafe first: cache the payload so nothing is lost even if delivery
      // fails and the customer refreshes.
      try {
        sessionStorage.setItem('bmn_last_quote_lead', JSON.stringify(payload));
      } catch (e) { /* private mode etc */ }

      if (!leadUrl) {
        console.warn('[BMN Quote] no data-lead-url configured; lead not delivered');
        return false;
      }
      try {
        if (/hsforms\.com/i.test(leadUrl)) return await postHubSpotForm(contact, bodyText);
        return await postJsonLead(contact, payload, bodyText);
      } catch (err) {
        console.warn('[BMN Quote] lead delivery failed', err);
        return false;
      }
    }

    // HubSpot Forms API (v3 integration submit). CORS-open, no auth, not behind
    // the storefront's Cloudflare. leadUrl is the full submit URL for the region
    // and form, e.g. https://api-eu1.hsforms.com/submissions/v3/integration/submit/{portalId}/{formGuid}
    async function postHubSpotForm(contact, bodyText) {
      var parts = (contact.name || '').trim().split(/\s+/);
      var firstName = parts.shift() || (contact.name || '');
      var lastName = parts.join(' ');
      var fields = [
        { name: 'email', value: contact.email },
        { name: 'firstname', value: firstName }
      ];
      if (lastName)      fields.push({ name: 'lastname', value: lastName });
      if (contact.phone) fields.push({ name: 'phone', value: contact.phone });
      // company is NOT a separate form field - it is folded into the enquiry
      // summary (bodyText already carries a "Company:" line when provided), so
      // HubSpot never receives an unmapped field that would 400 the submission.
      var said = conversation
        .filter(function (m) { return m.role === 'user'; })
        .map(function (m) { return '- ' + m.content; })
        .join('\n');
      var msg = bodyText;
      if (contact.notes) msg = 'Enquiry: ' + contact.notes + '\n\n' + msg;
      if (contact.extra) msg = 'Customer wrote: ' + contact.extra + '\n\n' + msg;
      if (said) msg += '\n\nWhat they typed in the chat:\n' + said;
      fields.push({ name: 'message', value: msg.slice(0, 60000) });
      var body = {
        fields: fields,
        context: { pageUri: location.href, pageName: document.title }
      };
      var ctrl = new AbortController();
      var t = setTimeout(function () { ctrl.abort(); }, 15000);
      var r;
      try {
        r = await fetch(leadUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          signal: ctrl.signal
        });
      } finally { clearTimeout(t); }
      return !!(r && r.ok);
    }

    // Generic JSON lead POST (e.g. a Netlify /lead function that emails info@).
    async function postJsonLead(contact, payload, bodyText) {
      var ctrl = new AbortController();
      var t = setTimeout(function () { ctrl.abort(); }, 15000);
      var r;
      try {
        r = await fetch(leadUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: contact.name,
            email: contact.email,
            company: contact.company,
            phone: contact.phone,
            tag: Brain.consts.LEAD_TAG,
            body: bodyText,
            payload: payload
          }),
          signal: ctrl.signal
        });
      } finally { clearTimeout(t); }
      return !!(r && r.ok);
    }

    function setLoading(v) {
      loading = !!v;
      if (typingEl) typingEl.classList.toggle('is-on', loading);
      if (sendEl) sendEl.disabled = loading;
      if (msgsEl) msgsEl.scrollTop = msgsEl.scrollHeight;
    }

    function addUser(text) {
      var row = document.createElement('div');
      row.className = 'bmn-q-row bmn-q-row--user';
      row.innerHTML = '<div class="bmn-q-bubble bmn-q-bubble--user">' + esc(text) + '</div>';
      msgsEl.appendChild(row);
      msgsEl.scrollTop = msgsEl.scrollHeight;
    }
    function addBot(text) {
      var row = document.createElement('div');
      row.className = 'bmn-q-row bmn-q-row--bot';
      row.innerHTML = '<div class="bmn-q-bot-av">B</div><div class="bmn-q-bubble bmn-q-bubble--bot">' + esc(text) + '</div>';
      msgsEl.appendChild(row);
      msgsEl.scrollTop = msgsEl.scrollHeight;
    }

    function firstName(s) { return (s || '').trim().split(/\s+/)[0] || 'there'; }
    function esc(s) {
      return String(s || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  function renderShell(mode, agentName) {
    // The header, messages, quick-reply strip, gate form and input row are
    // identical between the two mount modes. The wrapper element differs.
    var chatShell =
      '<div class="bmn-q-header">' +
        '<div class="bmn-q-header__left">' +
          '<div class="bmn-q-avatar">B</div>' +
          '<div>' +
            '<div class="bmn-q-name">' + esc(agentName) + '</div>' +
            '<div class="bmn-q-status"><span class="bmn-q-dot"></span>Instant quote assistant</div>' +
          '</div>' +
        '</div>' +
        '<button type="button" class="bmn-q-close" aria-label="Close">✕</button>' +
      '</div>' +
      '<div class="bmn-q-msgs" role="log" aria-live="polite">' +
        '<div class="bmn-q-typing"><div class="bmn-q-bot-av">B</div><div class="bmn-q-typing-dots"><span></span><span></span><span></span></div></div>' +
      '</div>' +
      '<div class="bmn-q-quick"></div>' +
      '<div class="bmn-q-gate">' +
        '<h3 class="bmn-q-gate__title">Get your itemised branded quote</h3>' +
        '<p class="bmn-q-gate__lead">Pop your name and email in and the team will send your itemised branded quote over, usually within one working day.</p>' +
        '<form novalidate>' +
          '<div class="bmn-q-gate__grid">' +
            '<div class="bmn-q-field">' +
              '<label class="bmn-q-label" for="bmn-q-name">Name</label>' +
              '<input class="bmn-q-input" id="bmn-q-name" data-gate="name" type="text" autocomplete="name" required />' +
              '<div class="bmn-q-error" data-error="name"></div>' +
            '</div>' +
            '<div class="bmn-q-field">' +
              '<label class="bmn-q-label" for="bmn-q-email">Email</label>' +
              '<input class="bmn-q-input" id="bmn-q-email" data-gate="email" type="email" autocomplete="email" required />' +
              '<div class="bmn-q-error" data-error="email"></div>' +
            '</div>' +
            '<div class="bmn-q-field">' +
              '<label class="bmn-q-label" for="bmn-q-company">Company (optional)</label>' +
              '<input class="bmn-q-input" id="bmn-q-company" data-gate="company" type="text" autocomplete="organization" />' +
            '</div>' +
            '<div class="bmn-q-field">' +
              '<label class="bmn-q-label" for="bmn-q-phone">Phone (optional)</label>' +
              '<input class="bmn-q-input" id="bmn-q-phone" data-gate="phone" type="tel" autocomplete="tel" />' +
            '</div>' +
          '</div>' +
          '<div class="bmn-q-field">' +
            '<label class="bmn-q-label" for="bmn-q-notes">Your enquiry (garment, quantity, logo, deadline)</label>' +
            '<textarea class="bmn-q-input" id="bmn-q-notes" data-gate="notes" rows="3"></textarea>' +
          '</div>' +
          '<div class="bmn-q-gate__actions">' +
            '<button type="submit" class="bmn-q-btn bmn-q-btn--submit">Send my quote request</button>' +
          '</div>' +
          '<p class="bmn-q-consent">We only use your details to send this quote and follow up on it. Turnaround is 7 to 10 working days.</p>' +
        '</form>' +
      '</div>' +
      '<div class="bmn-q-input-row">' +
        '<input class="bmn-q-input" type="text" placeholder="Type your enquiry..." autocomplete="off" aria-label="Message" />' +
        '<button type="button" class="bmn-q-send" aria-label="Send">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>' +
        '</button>' +
      '</div>' +
      '<div class="bmn-q-footer">Quotes are itemised by the team. Turnaround 7 to 10 working days.</div>';

    if (mode === 'page') {
      return '<div class="bmn-q-page">' + chatShell + '</div>';
    }
    return '' +
      '<button type="button" class="bmn-q-fab" aria-label="Open quote assistant">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>' +
        'Get a quote' +
      '</button>' +
      '<div class="bmn-q-panel" role="dialog" aria-label="Quote assistant">' + chatShell + '</div>';
  }

  function esc(s) {
    return String(s || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
})();
